<template>
  <div>
    <px-view-header title="Cadastro de tabela de preço" title-class="font-weight-bold"
                    :breadcrumbs="breadcrumbs">
      <template v-slot:before>
        <v-btn class="mb-4 mr-1" icon x-large color="primary" @click="onClickBack">
          <v-icon>
            mdi-arrow-left
          </v-icon>
        </v-btn>
      </template>
    </px-view-header>
    <div v-if="loadingTabela">
      <px-circular-loading/>
    </div>
    <div v-if="!loadingTabela" class="mt-3">
      <v-row dense>
        <v-col cols="12">
          <v-subheader class="body-1 grey lighten-4 text--primary">
            <v-icon>
              mdi-text-box-outline
            </v-icon>
            <span class="ml-2">Dados gerais</span>
          </v-subheader>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" sm="6">
          <v-text-field
            dense
            outlined
            required
            hide-details
            maxlength="255"
            class="required rounded-tag-small"
            label="Descrição"
            :error="$v.tabelaPreco.descricao.$error"
            @blur="$v.tabelaPreco.descricao.$touch()"
            v-model="tabelaPreco.descricao"
          />
        </v-col>
        <v-col cols="12" sm="4">
          <v-text-field
            dense
            outlined
            required
            hide-details
            maxlength="255"
            class="rounded-tag-small"
            label="Código interno"
            v-model="tabelaPreco.codigoInterno"
          />
        </v-col>
        <v-col cols="12" sm="2">
          <v-switch dense
                    hide-details
                    class="pa-0 mt-2 mr-2"
                    label="Ativo"
                    color="primary"
                    v-model="tabelaPreco.ativo"/>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" sm="3">
          <px-date-picker dense
                          input-class="required rounded-tag-small"
                          label="Data de vigência inicial"
                          :error="$v.tabelaPreco.dataVigenciaInicial.$error"
                          @blur="$v.tabelaPreco.dataVigenciaInicial.$touch()"
                          v-model="tabelaPreco.dataVigenciaInicial"/>
        </v-col>
        <v-col cols="12" sm="3">
          <px-date-picker dense
                          input-class="required rounded-tag-small"
                          label="Data de vigência final"
                          :error="$v.tabelaPreco.dataVigenciaFinal.$error"
                          @blur="$v.tabelaPreco.dataVigenciaFinal.$touch()"
                          v-model="tabelaPreco.dataVigenciaFinal"/>
        </v-col>
        <v-col cols="12" sm="3">
          <px-select-enum dense
                          class="rounded-tag-small"
                          v-model="tabelaPreco.formaPagamento"
                          :items="formasPagamentos"
                          @change="onChangeFormaPagamento"
                          label="Forma de pagamento"/>
        </v-col>
        <v-col cols="12" sm="3">
          <px-autocomplete-prazo-pagamento
            dense
            label="Prazo de pagamento"
            class="rounded-tag-small"
            :disabled="isAVista"
            v-model="tabelaPreco.prazoPagamento"/>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <px-autocomplete-fornecedor show-cnpj
                                      show-codigo
                                      dense
                                      path="fornecedor/score"
                                      v-model="tabelaPreco.fornecedor"
                                      class="required rounded-tag-small"
                                      :error="$v.tabelaPreco.fornecedor.$error"
                                      @blur="$v.tabelaPreco.fornecedor.$touch()"
                                      required/>
        </v-col>
      </v-row>
      <v-row dense class="mt-3">
        <v-col cols="12">
          <v-subheader class="body-1 grey lighten-4 text--primary">
            <v-icon>
              mdi-text-box-outline
            </v-icon>
            <span class="ml-2">Produtos</span>
          </v-subheader>
        </v-col>
      </v-row>
      <v-simple-table
        fixed-header
      >
        <template v-slot:default>
          <thead>
          <tr>
            <th style="min-width: 400px">Produto</th>
            <th>Preço à vista</th>
            <th>Preço a prazo</th>
            <th>Saving</th>
            <th>ICMS</th>
            <th>IPI</th>
            <th class="text-center"></th>
          </tr>
          </thead>
          <tbody>
          <tr style="height: 55px" v-for="(produto, index) in tabelaPreco.itens"
              :key="produto.$$id">
            <td class="px-1">
              <px-autocomplete-produto
                dense
                required
                show-details
                :truncate-value="25"
                menu-props="auto"
                :clearable="false"
                v-model="produto.produto"
                :error="$v.tabelaPreco.itens.$each[index].produto.$error"
                @blur="$v.tabelaPreco.itens.$each[index].produto.$touch()"
                class="required rounded-tag-small"
                base-path="/produtos/page"/>
            </td>
            <td class="px-1">
              <px-input-currency
                dense
                class="required rounded-tag-small"
                label="Preço à vista"
                :precision="casasDecimais(produto)"
                :error="$v.tabelaPreco.itens.$each[index].precoAVista.$error"
                @blur="$v.tabelaPreco.itens.$each[index].precoAVista.$touch()"
                v-model="produto.precoAVista"/>
            </td>
            <td class="px-1">
              <px-input-currency
                dense
                class="required rounded-tag-small"
                label="Preço a prazo"
                :precision="casasDecimais(produto)"
                :error="$v.tabelaPreco.itens.$each[index].precoAPrazo.$error"
                @blur="$v.tabelaPreco.itens.$each[index].precoAPrazo.$touch()"
                v-model="produto.precoAPrazo"/>
            </td>
            <td class="px-1">
              <px-input-currency
                dense
                class="rounded-tag-small"
                label="Saving"
                :precision="casasDecimais(produto)"
                v-model="produto.saving"/>
            </td>
            <td class="px-1">
              <px-input-percentual
                dense
                label="ICMS %"
                class="rounded-tag-small"
                :error="$v.tabelaPreco.itens.$each[index].icms.$error"
                @blur="$v.tabelaPreco.itens.$each[index].icms.$touch()"
                v-model="produto.icms"/>
            </td>
            <td class="px-1">
              <px-input-percentual
                dense
                label="IPI %"
                class="rounded-tag-small"
                :error="$v.tabelaPreco.itens.$each[index].ipi.$error"
                @blur="$v.tabelaPreco.itens.$each[index].ipi.$touch()"
                v-model="produto.ipi"/>
            </td>
            <td>
              <v-btn icon @click="onRemoveItem(index)">
                <v-icon>
                  mdi-close
                </v-icon>
              </v-btn>
            </td>
          </tr>
          </tbody>
        </template>
      </v-simple-table>
      <v-row dense class="mt-2">
        <v-col cols="12" class="text-right">
          <v-btn small class="elevation-0 rounded-tag-small normal-btn-text"
                 color="primary"
                 @click="onClickAddProduto">
            <v-icon left small>
              mdi-plus
            </v-icon>
            Adicionar produto
          </v-btn>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-btn class="elevation-0 normal-btn-text rounded-tag-small"
                 color="primary"
                 :disabled="loadingSave"
                 @click="onClickSave">
            Salvar
          </v-btn>
          <v-btn class="elevation-0 normal-btn-text rounded-tag-small ml-3"
                 @click="onClickBack">
            Cancelar
          </v-btn>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { FormaPagamento, Moeda } from 'px-business-components';
import { uniqueId } from 'lodash';
import validationMixin from '@/mixins/validation-mixin';
import TabelaPrecoService from '@/modules/tabela-preco/tabela-preco-service';

const initalState = () => ({
  $$id: uniqueId(),
  produto: null,
  precoAVista: null,
  precoAPrazo: null,
  saving: 0,
  icms: 0,
  ipi: 0,
});

export default {
  mixins: [validationMixin],
  data() {
    return {
      id: this.$route.params.id,
      formasPagamentos: FormaPagamento.values,
      loadingTabela: false,
      loadingSave: false,
      tabelaPreco: {
        descricao: null,
        codigoInterno: null,
        moeda: Moeda.keys.BRL.key,
        fornecedor: null,
        dataVigenciaInicial: null,
        dataVigenciaFinal: null,
        formaPagamento: null,
        prazoPagamento: null,
        ativo: true,
        itens: [initalState()],
      },
      breadcrumbs: [
        { text: 'Empresa' },
        { text: 'Tabelas de preço', to: '/tabelas-preco' },
        { text: 'Cadastro' },
      ],
    };
  },
  computed: {
    casasDecimais() {
      return produto => (produto.produto ? produto.produto.casasDecimais : 2);
    },
    isAVista() {
      return this.tabelaPreco.formaPagamento !== FormaPagamento.keys.A_PRAZO.key;
    },
    isEdicao() {
      return !!this.id;
    },
  },
  methods: {
    onChangeFormaPagamento() {
      this.tabelaPreco.prazoPagamento = null;
    },
    onClickBack() {
      this.$router.push({ name: 'main.tabelas-preco' });
    },
    onClickAddProduto() {
      this.tabelaPreco.itens.push(initalState());
    },
    onRemoveItem(index) {
      this.tabelaPreco.itens.splice(index, 1);
    },
    buildPayload() {
      const tabela = {
        ...this.tabelaPreco,
        formaPagamento: this.tabelaPreco.formaPagamento ?? null,
      };
      tabela.itens = tabela.itens.map(item => ({
        produto: {
          id: item.produto.id,
        },
        precoAVista: item.precoAVista,
        precoAPrazo: item.precoAPrazo,
        saving: item.saving,
        icms: item.icms,
        ipi: item.ipi,
      }));
      return tabela;
    },
    onClickSave() {
      if (this.validateForm()) {
        return;
      }
      this.loadingSave = true;
      TabelaPrecoService.save(this.buildPayload())
        .then(() => {
          const successMessage = this.tabelaPreco.id
            ? 'Tabela de preço atualizada'
            : 'Tabela de preço criada';
          this.$toast(successMessage, { color: 'success' });
          this.onClickBack();
        })
        .finally(() => {
          this.loadingSave = false;
        });
    },
    getTabelaPreco() {
      this.loadingTabela = true;
      TabelaPrecoService.get(this.id)
        .then((response) => {
          this.tabelaPreco = response.data;
        })
        .finally(() => {
          this.loadingTabela = false;
        });
    },
  },
  created() {
    if (this.isEdicao) {
      this.getTabelaPreco();
    }
  },
  validations: {
    tabelaPreco: {
      descricao: {
        required,
      },
      dataVigenciaInicial: {
        required,
      },
      dataVigenciaFinal: {
        required,
      },
      fornecedor: {
        required,
      },
      itens: {
        $each: {
          produto: {
            required,
          },
          precoAVista: {
            required,
            minValue(value) {
              return value > 0;
            },
          },
          precoAPrazo: {
            required,
            minValue(value) {
              return value > 0;
            },
          },
          icms: {
            required,
          },
          ipi: {
            required,
          },
        },
      },
    },
  },
};
</script>
